/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  GetProductItemListRequest,
  GetProductItemListResponse,
  GetProductItemDetailRequest,
  GetProductItemDetailResponse,
  CreateProductItemRequest,
  CreateProductItemResponse,
  CreateProductItemInput,
  UpdateProductItemInput,
  UpdateProductItemRequest,
  UpdateProductItemResponse,
  DeleteProductItemRequest,
  DeleteProductItemResponse,
  ProductItemTransitionInput,
  ProductItemTransitionResponse
} from "../../Graphql/types/productItem";
import {
  ProductItem,
  ProductItemFormData
} from "../../ProductMFE/Products/types/products";
import {
  getProductItemListQuery,
  getProductItemDetailQuery,
  createProductItemQuery,
  updateProductItemQuery,
  deleteProductItemQuery,
  transitionProductItemQuery
} from "../../Graphql/querries/productItemQuery";
import {
  setProductItemListWithTotal,
  setProductItemDetail,
  updateProductItem as updateProductItemAction,
  removeProductItem
} from "../../store/slices/productItemSlice";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  UserRole
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import type { UserDetails } from "../../store/slices/userSlice";
import { toGraphQLBaseUnit } from "../../Utils/toGraphQLBaseUnit";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

type RootStateForItemCreate = RootState & {
  store?: { storeDetails?: { id?: string } };
};

/** Align with backend `SecurityUtils.isCompanyAdmin` / profile `role` (trim + case). */
export function isUserCompanyAdmin(details: UserDetails | undefined): boolean {
  return (
    String(details?.role ?? "")
      .trim()
      .toUpperCase() === UserRole.COMPANY_ADMIN
  );
}

function isCompanyAdmin(details: UserDetails | undefined): boolean {
  return isUserCompanyAdmin(details);
}

/** Non-empty JWT `storeId` for log context; omit when absent (avoid `storeId: ""`). */
function optionalJwtStoreId(
  details: UserDetails | undefined
): string | undefined {
  const s = details?.storeId?.trim();
  return s && s.length > 0 ? s : undefined;
}

/**
 * Resolves `storeId` for **create** for store-scoped users only.
 * **COMPANY_ADMIN:** `productItem.storeId` comes from the catalog product, not the JWT.
 * Falls back to host-injected `store.storeDetails.id` when JWT has no store (e.g. embedded shell).
 */
function resolveStoreIdForProductItemCreate(
  state: RootStateForItemCreate
): string | undefined {
  const jwt = optionalJwtStoreId(state.user.userDetails);
  if (jwt) return jwt;
  return state.store?.storeDetails?.id?.trim() || undefined;
}

/**
 * Map ProductItemFormData to CreateProductItemInput (proxy schema).
 * map salesPrice -> price; optional storeId for company-admin context.
 */
function toCreateProductItemInput(
  form: ProductItemFormData,
  inventoryStoreId?: string
): CreateProductItemInput {
  const input: CreateProductItemInput = {
    productId: form.productId,
    totalQuantity: Number(form.totalQuantity),
    variationName: form.variationName ?? "",
    purchasePrice: form.purchasePrice ?? 0,
    price: form.salesPrice ?? 0,
    minSalesPrice: form.minSalesPrice ?? 0
  };

  if (inventoryStoreId?.trim()) {
    input.storeId = inventoryStoreId.trim();
  }

  const unitWire = toGraphQLBaseUnit(form.unit as string | undefined);
  if (unitWire) {
    input.unit = unitWire;
  }

  input.variationName = (form.variationName ?? "").trim();
  if (form.lotNumber?.trim()) input.lotNumber = form.lotNumber.trim();
  input.purchasePrice = form.purchasePrice!;
  input.price = form.salesPrice!;
  input.minSalesPrice = form.minSalesPrice!;
  if (form.expirationDate?.trim())
    input.expirationDate = form.expirationDate.trim();
  if (form.productionDate?.trim())
    input.productionDate = form.productionDate.trim();
  if (typeof form.weight === "number") input.weight = form.weight;
  if (typeof form.height === "number") input.height = form.height;
  if (typeof form.width === "number") input.width = form.width;
  if (typeof form.thickness === "number") input.thickness = form.thickness;
  if (form.color?.trim()) input.color = form.color.trim();
  if (form.material?.trim()) input.material = form.material.trim();
  if (form.packagingType?.trim())
    input.packagingType = form.packagingType.trim();
  if (form.packSize?.trim()) input.packSize = form.packSize.trim();

  return input;
}

/** Map stored/display `ProductItem` to GraphQL `UpdateProductItemInput`. */
function toUpdateProductItemInput(item: ProductItem): UpdateProductItemInput {
  const input: UpdateProductItemInput = { id: item.id };

  if (item.variationName?.trim())
    input.variationName = item.variationName.trim();
  if (item.lotNumber !== undefined) {
    const lot = item.lotNumber?.trim() || "";
    input.lotNumber = lot;
  }
  if (typeof item.purchasePrice === "number")
    input.purchasePrice = item.purchasePrice;
  const listPrice = item.salesPrice ?? item.price;
  if (typeof listPrice === "number") input.price = listPrice;
  if (typeof item.minSalesPrice === "number")
    input.minSalesPrice = item.minSalesPrice;

  if (typeof item.weight === "number") input.weight = item.weight;
  if (typeof item.height === "number") input.height = item.height;
  if (typeof item.width === "number") input.width = item.width;
  if (typeof item.thickness === "number") input.thickness = item.thickness;
  if (item.color !== undefined) {
    input.color = item.color.trim();
  }
  if (item.material !== undefined) {
    input.material = item.material.trim();
  }
  if (item.packagingType !== undefined) {
    input.packagingType = item.packagingType.trim();
  }
  if (item.packSize !== undefined) {
    input.packSize = item.packSize.trim();
  }

  return input;
}

/**
 * useGetProductItemList
 * Fetch list of product items
 */
export const useGetProductItemList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ProductItem[], Error, GetProductItemListRequest>({
    mutationKey: ["getProductItemList"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = optionalJwtStoreId(userDetails);

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching product item list",
        buildLogContext(
          "product-items",
          "get-product-item-list",
          userId,
          companyId,
          storeId,
          {
            productId: String(input.productId ?? "")
          }
        )
      );

      try {
        const apiInput: Record<string, unknown> = {
          productId: input.productId
        };
        if (input.status != null) apiInput.status = input.status;
        if (input.nameSearch != null && input.nameSearch.trim() !== "")
          apiInput.nameSearch = input.nameSearch.trim();
        if (input.page != null) apiInput.page = input.page;
        if (input.pageSize != null) apiInput.pageSize = input.pageSize;
        if (input.storeId != null && input.storeId.trim() !== "")
          apiInput.storeId = input.storeId.trim();

        const response: GetProductItemListResponse = await fetchGraphQL(
          getProductItemListQuery,
          { input: apiInput },
          setAlert
        );

        const {
          items,
          code,
          totalElements,
          totalPages,
          currentPage,
          pageSize
        } = response.listProductItemsByProduct;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch product item list",
            buildLogContext(
              "product-items",
              "get-product-item-list",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code),
                productId: String(input.productId ?? "")
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        // ❌ Logically broken success response
        if (!items) {
          captureAppException(
            "API returned OK but items array missing",
            new Error("API returned OK but items array missing"),
            buildLogContext(
              "product-items",
              "get-product-item-list",
              userId,
              companyId,
              storeId,
              { productId: String(input.productId ?? "") }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        // 🔄 Transform API response items (normalize id field)
        const transformedItems: ProductItem[] = items.map((item) => ({
          ...item,
          id: (item as any).id || item.id
        }));

        // ✅ Success: dispatch list with pagination from API response fields
        dispatch(
          setProductItemListWithTotal({
            items: transformedItems,
            totalElements: totalElements ?? transformedItems.length,
            totalPages: totalPages ?? 1,
            currentPage: currentPage ?? 0,
            pageSize: pageSize ?? input.pageSize ?? 20
          })
        );

        sendAppLog(
          LOG_LEVEL.INFO,
          "Product item list fetched successfully",
          buildLogContext(
            "product-items",
            "get-product-item-list",
            userId,
            companyId,
            storeId,
            {
              itemCount: transformedItems.length,
              totalElements: totalElements ?? 0,
              currentPage: currentPage ?? 0,
              totalPages: totalPages ?? 0,
              productId: String(input.productId ?? "")
            }
          )
        );

        return transformedItems;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch product item list",
          error,
          buildLogContext(
            "product-items",
            "get-product-item-list",
            userId,
            companyId,
            storeId,
            { productId: String(input.productId ?? "") }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return [];
      }
    }
  });
};

/**
 * useGetProductItemDetail
 * Fetch single product item details by id
 */
export const useGetProductItemDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ProductItem | null, Error, GetProductItemDetailRequest>({
    mutationKey: ["getProductItemDetail"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = optionalJwtStoreId(userDetails);

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching product item detail",
        buildLogContext(
          "product-items",
          "get-product-item-detail",
          userId,
          companyId,
          storeId,
          { itemId: String(input?.id) }
        )
      );

      try {
        const response: GetProductItemDetailResponse = await fetchGraphQL(
          getProductItemDetailQuery,
          { input },
          setAlert
        );

        const { item, code } = response.getProductItemDetail;

        // ⚠️ Expected API/business failure
        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch product item detail",
            buildLogContext(
              "product-items",
              "get-product-item-detail",
              userId,
              companyId,
              storeId,
              { apiCode: String(code), itemId: String(input?.id) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        // ❌ Logically broken success response
        if (!item) {
          captureAppException(
            "API returned OK but item object missing",
            new Error("API returned OK but item object missing"),
            buildLogContext(
              "product-items",
              "get-product-item-detail",
              userId,
              companyId,
              storeId,
              { itemId: String(input?.id) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // 🔄 Normalize id field
        const transformedItem: ProductItem = {
          ...item,
          id: (item as any).id || item.id
        };

        // ✅ Success
        dispatch(setProductItemDetail(transformedItem));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Product item detail fetched successfully",
          buildLogContext(
            "product-items",
            "get-product-item-detail",
            userId,
            companyId,
            storeId,
            { itemId: String(transformedItem.id) }
          )
        );

        return transformedItem;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to fetch product item detail",
          error,
          buildLogContext(
            "product-items",
            "get-product-item-detail",
            userId,
            companyId,
            storeId,
            { itemId: String(input?.id) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return null;
      }
    }
  });
};

/**
 * useCreateProductItem
 * Create a single product item
 */
export const useCreateProductItem = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const store = useStore<RootStateForItemCreate>();

  return useMutation<string | null, Error, CreateProductItemRequest>({
    mutationKey: ["createProductItem"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      const details = store.getState().user.userDetails;
      const admin = isCompanyAdmin(details);
      const fromForm = input.productItem.storeId?.trim() || undefined;
      const resolvedStoreId = admin
        ? fromForm
        : resolveStoreIdForProductItemCreate(store.getState());
      const storeIdForContext =
        resolvedStoreId && resolvedStoreId.length > 0
          ? resolvedStoreId
          : undefined;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating product item",
        buildLogContext(
          "product-items",
          "create-product-item",
          userId,
          companyId,
          storeIdForContext,
          {
            productId: String(input.productItem.productId),
            totalQuantity: input.productItem.totalQuantity
          }
        )
      );

      try {
        if (admin) {
          if (!resolvedStoreId) {
            sendAppLog(
              LOG_LEVEL.WARNING,
              "Create product item skipped: company admin missing storeId",
              buildLogContext(
                "product-items",
                "create-product-item",
                userId,
                companyId,
                storeIdForContext,
                { productId: String(input.productItem.productId) }
              )
            );
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: "STORE_ID_REQUIRED" as ALERT_BANNER_CODE
            });
            return null;
          }
        }

        const apiInput = toCreateProductItemInput(
          input.productItem,
          resolvedStoreId
        );

        const response: CreateProductItemResponse = await fetchGraphQL(
          createProductItemQuery,
          { input: apiInput },
          setAlert
        );

        const createResult = response?.createProductItem;

        // ❌ Unexpected response shape
        if (!createResult) {
          captureAppException(
            "Create product item failed: missing createProductItem",
            new Error("Create product item failed: missing createProductItem"),
            buildLogContext(
              "product-items",
              "create-product-item",
              userId,
              companyId,
              storeIdForContext,
              { productId: String(input.productItem.productId) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ⚠️ Expected API/business failure
        if (createResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Product item creation rejected by API",
            buildLogContext(
              "product-items",
              "create-product-item",
              userId,
              companyId,
              storeIdForContext,
              {
                apiCode: String(createResult.code),
                productId: String(input.productItem.productId)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: createResult.code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        const id = createResult.id;

        // ❌ Logically broken success response
        if (!id) {
          captureAppException(
            "API returned OK but item ID missing",
            new Error("API returned OK but item ID missing"),
            buildLogContext(
              "product-items",
              "create-product-item",
              userId,
              companyId,
              storeIdForContext,
              { productId: String(input.productItem.productId) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Product item created successfully",
          buildLogContext(
            "product-items",
            "create-product-item",
            userId,
            companyId,
            storeIdForContext,
            {
              itemId: String(id),
              productId: String(input.productItem.productId)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return id;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to create product item",
          error,
          buildLogContext(
            "product-items",
            "create-product-item",
            userId,
            companyId,
            storeIdForContext,
            { productId: String(input.productItem.productId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return null;
      }
    }
  });
};

/**
 * useUpdateProductItem
 * Update existing product item
 */
export const useUpdateProductItem = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<string | null, Error, UpdateProductItemRequest>({
    mutationKey: ["updateProductItem"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = optionalJwtStoreId(userDetails);

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating product item",
        buildLogContext(
          "product-items",
          "update-product-item",
          userId,
          companyId,
          storeId,
          { itemId: String(input.productItem.id) }
        )
      );

      try {
        const graphqlInput = toUpdateProductItemInput(input.productItem);

        const response: UpdateProductItemResponse = await fetchGraphQL(
          updateProductItemQuery,
          { input: graphqlInput },
          setAlert
        );

        const updateResult = response?.updateProductItem;

        // ❌ Unexpected response shape
        if (!updateResult) {
          captureAppException(
            "Update product item failed: missing updateProductItem",
            new Error("Update product item failed: missing updateProductItem"),
            buildLogContext(
              "product-items",
              "update-product-item",
              userId,
              companyId,
              storeId,
              { itemId: String(input.productItem.id) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ⚠️ Expected API/business failure
        if (updateResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Product item update rejected by API",
            buildLogContext(
              "product-items",
              "update-product-item",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(updateResult.code),
                itemId: String(input.productItem.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: updateResult.code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        const id = updateResult.id;

        // ❌ Logically broken success response
        if (!id) {
          captureAppException(
            "API returned OK but item ID missing",
            new Error("API returned OK but item ID missing"),
            buildLogContext(
              "product-items",
              "update-product-item",
              userId,
              companyId,
              storeId,
              { itemId: String(input.productItem.id) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        // ✅ Success: update the item in the store with the input data
        dispatch(updateProductItemAction(input.productItem));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Product item updated successfully",
          buildLogContext(
            "product-items",
            "update-product-item",
            userId,
            companyId,
            storeId,
            { itemId: String(id) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return id;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to update product item",
          error,
          buildLogContext(
            "product-items",
            "update-product-item",
            userId,
            companyId,
            storeId,
            { itemId: String(input.productItem.id) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return null;
      }
    }
  });
};

/**
 * useDeleteProductItem
 * Delete a product item
 */
export const useDeleteProductItem = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<boolean, Error, DeleteProductItemRequest>({
    mutationKey: ["deleteProductItem"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = optionalJwtStoreId(userDetails);

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started deleting product item",
        buildLogContext(
          "product-items",
          "delete-product-item",
          userId,
          companyId,
          storeId,
          { itemId: String(input?.id) }
        )
      );

      try {
        const response: DeleteProductItemResponse = await fetchGraphQL(
          deleteProductItemQuery,
          { input },
          setAlert
        );

        const deleteResult = response?.deleteProductItem;

        // ❌ Unexpected response shape
        if (!deleteResult) {
          captureAppException(
            "Delete product item failed: missing deleteProductItem",
            new Error("Delete product item failed: missing deleteProductItem"),
            buildLogContext(
              "product-items",
              "delete-product-item",
              userId,
              companyId,
              storeId,
              { itemId: String(input?.id) }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return false;
        }

        // ⚠️ Expected API/business failure
        if (deleteResult.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Product item deletion rejected by API",
            buildLogContext(
              "product-items",
              "delete-product-item",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(deleteResult.code),
                itemId: String(input?.id)
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: deleteResult.code as unknown as ALERT_BANNER_CODE
          });
          return false;
        }

        // ✅ Success
        dispatch(removeProductItem(input?.id));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Product item deleted successfully",
          buildLogContext(
            "product-items",
            "delete-product-item",
            userId,
            companyId,
            storeId,
            { itemId: String(input?.id) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return true;
      } catch (error) {
        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to delete product item",
          error,
          buildLogContext(
            "product-items",
            "delete-product-item",
            userId,
            companyId,
            storeId,
            { itemId: String(input?.id) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        return false;
      }
    }
  });
};

/**
 * useTransitionProductItem
 * Transition a product item state
 */
export const useTransitionProductItem = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<boolean, Error, ProductItemTransitionInput>({
    mutationKey: ["transitionProductItem"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = optionalJwtStoreId(userDetails);

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started transitioning product item",
        buildLogContext(
          "product-items",
          "transition-product-item",
          userId,
          companyId,
          storeId,
          {
            item_id: input.productItemId,
            event: input.event
          }
        )
      );

      try {
        const response: ProductItemTransitionResponse = await fetchGraphQL(
          transitionProductItemQuery,
          { input },
          setAlert
        );

        const { code } = response.transitionProductItem;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Product item transition rejected by API",
            buildLogContext(
              "product-items",
              "transition-product-item",
              userId,
              companyId,
              storeId,
              {
                api_code: String(code),
                item_id: input.productItemId,
                event: input.event
              }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return false;
        }

        sendAppLog(
          LOG_LEVEL.INFO,
          "Product item transitioned successfully",
          buildLogContext(
            "product-items",
            "transition-product-item",
            userId,
            companyId,
            storeId,
            {
              item_id: input.productItemId,
              event: input.event
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });

        return true;
      } catch (error) {
        captureAppException(
          "Failed to transition product item",
          error,
          buildLogContext(
            "product-items",
            "transition-product-item",
            userId,
            companyId,
            storeId,
            {
              item_id: input.productItemId,
              event: input.event
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw error;
      }
    }
  });
};
